import React, { useEffect, useState } from 'react';
import { Download, Share, Plus, X } from 'lucide-react';
import { Button } from './ui/button';

// Chrome fires beforeinstallprompt and lets us trigger the native install
// dialog. Safari/iOS never fires it and has no programmatic install at all, so
// the only option there is to talk the user through Share > Add to Home Screen.

const isIOS = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
  // iPadOS 13+ reports as desktop Safari but is still touch-driven.
  (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.matchMedia('(display-mode: minimal-ui)').matches ||
  // Safari's non-standard flag for home-screen launches.
  window.navigator.standalone === true;

export default function InstallPrompt({ className = '' }) {
  const [deferred, setDeferred] = useState(null);
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  const [installed, setInstalled] = useState(isStandalone);

  useEffect(() => {
    const onBeforeInstall = (e) => {
      // Suppress the mini-infobar so the CTA below is the only entry point.
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // Already running as an installed app — nothing to offer.
  if (installed) return null;

  const ios = isIOS();
  // No captured prompt and not iOS means the browser either can't install or
  // has already done so. Showing a dead button would be worse than nothing.
  if (!deferred && !ios) return null;

  const handleClick = async () => {
    if (ios) {
      setShowIOSHelp(true);
      return;
    }
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    // A dismissed prompt can't be re-shown with the same event.
    if (outcome === 'accepted') setInstalled(true);
    setDeferred(null);
  };

  return (
    <>
      <Button
        variant="outline"
        size="lg"
        onClick={handleClick}
        className={`w-full text-md h-12 px-8 gap-2 ${className}`}
      >
        <Download className="w-4 h-4" />
        Install app
      </Button>

      {showIOSHelp && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4"
          onClick={() => setShowIOSHelp(false)}
        >
          <div
            className="bg-card border border-border rounded-xl shadow-xl w-full max-w-sm p-5 text-left"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <h3 className="text-lg font-semibold text-foreground">
                Add Jigri to your Home Screen
              </h3>
              <button
                onClick={() => setShowIOSHelp(false)}
                aria-label="Close"
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <ol className="space-y-3 text-sm text-muted-foreground">
              <li className="flex items-center gap-3">
                <Share className="w-5 h-5 shrink-0 text-primary" />
                <span>
                  Tap the <strong className="text-foreground">Share</strong> button in
                  Safari&apos;s toolbar.
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Plus className="w-5 h-5 shrink-0 text-primary" />
                <span>
                  Choose{' '}
                  <strong className="text-foreground">Add to Home Screen</strong>, then
                  tap Add.
                </span>
              </li>
            </ol>
            <p className="mt-4 text-xs text-muted-foreground">
              This must be done in Safari — Chrome and Firefox on iOS cannot add apps to
              the Home Screen.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
